import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { EntitySupportingCard } from "@mapmycustomers/shared/types/entity";
import { MapEntitiesSet } from "@mapmycustomers/shared/types/map";

import RoundButton from "@app/component/RoundButton";
import ButtonType from "@app/enum/ButtonType";
import { addToSelection, removeFromSelection } from "@app/scene/map/store/mapMode/actions";
import { getMapViewTool, getSelectedRecords } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { mapEntityIdGetter } from "@app/util/map/idGetters";

import styles from "./EntityActions.module.scss";

interface Props {
  entity?: EntitySupportingCard;
  onAddToSelection: typeof addToSelection;
  onRemoveFromSelection: typeof removeFromSelection;
  selection: MapEntitiesSet;
  tool?: MapTool;
}

const messages = defineMessages({
  add: {
    id: "map.pin.annotation.actionsBar.add",
    defaultMessage: "Add to selected",
    description: "Entity actions bar - add to selected",
  },
  remove: {
    id: "map.pin.annotation.actionsBar.remove",
    defaultMessage: "Remove from selection",
    description: "Entity actions bar - remove from selection",
  },
});

const EntityActions: React.FC<Props> = ({
  entity,
  onAddToSelection,
  onRemoveFromSelection,
  selection,
  tool,
}) => {
  const intl = useIntl();

  const entityId = entity ? mapEntityIdGetter(entity) : undefined;
  const isVisible = tool === MapTool.LASSO && entityId;

  const canRemove = isVisible && selection.has(entityId);
  const canAdd = isVisible && !canRemove;

  const handleAdd = useCallback(() => {
    if (entityId) {
      onAddToSelection(entityId);
    }
  }, [entityId, onAddToSelection]);

  const handleRemove = useCallback(() => {
    if (entityId) {
      onRemoveFromSelection(entityId);
    }
  }, [entityId, onRemoveFromSelection]);

  return isVisible ? (
    <>
      {canAdd && (
        <RoundButton
          className={styles.add}
          leftIcon={<FontAwesomeIcon icon={faPlus} />}
          onClick={handleAdd}
        >
          {intl.formatMessage(messages.add)}
        </RoundButton>
      )}

      {canRemove && (
        <RoundButton
          leftIcon={<FontAwesomeIcon icon={faMinusCircle} />}
          onClick={handleRemove}
          type={ButtonType.DANGER}
        >
          {intl.formatMessage(messages.remove)}
        </RoundButton>
      )}
    </>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  selection: getSelectedRecords(state),
  tool: getMapViewTool(state),
});

const mapDispatchToProps = {
  onAddToSelection: addToSelection,
  onRemoveFromSelection: removeFromSelection,
};

export default connect(mapStateToProps, mapDispatchToProps)(EntityActions);
